'use client'

import { useId, useState } from "react"

import { Slider } from "@/components/ui/slider"
import { formatCredits, formatMoney } from "@/lib/wesend"
import type { SitePrices } from "@/lib/site"

// What a number of messages would cost. With a monthly fee it is a month's bill: the base fee
// plus the credits the messages use. Without one nothing is paid by the month, so it is the cost
// of the messages alone and no period is named.
export default function PriceEstimator({ prices }: { prices: SitePrices }) {
  const [sms, setSms] = useState(1000)
  const [emails, setEmails] = useState(2000)

  const smsAmount = Math.ceil(sms * prices.smsCredits * prices.creditPrice)
  const emailAmount = Math.ceil(emails * prices.emailCredits * prices.creditPrice)
  const fee = prices.monthlyFeeEnabled
  const total = (fee ? prices.monthlyPrice : 0) + smsAmount + emailAmount

  return (
    <div className="rounded-2xl border bg-card p-5 shadow-xs sm:p-7">
      <h3 className="font-gilroy text-lg font-semibold">{fee ? 'Сарын төлбөрөө тооцоолох' : 'Илгээлтийн зардлаа тооцоолох'}</h3>

      <div className="mt-6 space-y-7">
        <Amount label={fee ? 'Сард илгээх SMS' : 'Илгээх SMS'} value={sms} onChange={setSms} max={20000} step={100} />
        <Amount label={fee ? 'Сард илгээх имэйл' : 'Илгээх имэйл'} value={emails} onChange={setEmails} max={50000} step={500} />
      </div>

      <dl className="mt-7 space-y-2.5 border-t pt-5 text-sm">
        {fee && <Row label="Суурь хураамж" value={formatMoney(prices.monthlyPrice)} />}
        <Row label={`SMS, ${formatCredits(sms)} ш`} value={formatMoney(smsAmount)} />
        <Row label={`Имэйл, ${formatCredits(emails)} ш`} value={formatMoney(emailAmount)} />
      </dl>

      <div className="mt-5 flex items-end justify-between gap-4 border-t pt-5">
        <p className="text-sm font-medium">{fee ? 'Сард ойролцоогоор' : 'Нийт ойролцоогоор'}</p>
        <p aria-live="polite" className="font-gilroy text-3xl font-semibold leading-none tabular-nums">{formatMoney(total)}</p>
      </div>

      <p className="mt-4 text-xs leading-5 text-muted-foreground">
        Нэг SMS-д латин үсгээр {prices.latinPartSize}, кирилл үсгээр {prices.unicodePartSize} тэмдэгт багтана. Үүнээс урт мессеж хоёр, гурван SMS болж тооцогдоно.
      </p>
    </div>
  )
}

function Amount({ label, value, onChange, max, step }: { label: string, value: number, onChange: (value: number) => void, max: number, step: number }) {
  const id = useId()
  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <span id={id} className="text-sm font-medium">{label}</span>
        <span className="text-sm font-semibold tabular-nums">{formatCredits(value)}</span>
      </div>
      <Slider aria-labelledby={id} value={[value]} onValueChange={([next]) => onChange(next)} min={0} max={max} step={step}
        className="mt-3.5 [&_[data-slot=slider-range]]:bg-blue-500 [&_[data-slot=slider-thumb]]:size-5 [&_[data-slot=slider-thumb]]:border-blue-500" />
    </div>
  )
}

function Row({ label, value }: { label: string, value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium tabular-nums">{value}</dd>
    </div>
  )
}
